

import { useState, useMemo, useCallback, useEffect } from "react";
import { toast } from "../../../lib/toast";
import * as itemAtaService from "../../../services/itemAta.service";
import * as desenvolvimentoService from "../../../services/desenvolvimento.service";
import type { ItemAta } from "../../../services/itemAta.service";
import type { Desenvolvimento } from "../../../services/desenvolvimento.service";
import { LIMITS, ERROR_MESSAGES } from "../../../lib/constants";

interface UseItemsParams {
  ataId: string;
  meetingNumber: number;
  meetingDate: string;
  initialItems: ItemAta[];
  initialDesenvolvimentos: Desenvolvimento[];
  onUpdate: () => Promise<void>;
  onChangeDetected?: () => void; // Notifica o sistema principal sobre mudanças
}

interface UseItemsReturn {
  items: ItemAta[];
  desenvolvimentos: Desenvolvimento[];
  editingItem: ItemAta | null;
  addingItemToTopic: string | null;
  itemsByTopic: Map<string, ItemAta[]>;
  setEditingItem: (item: ItemAta | null) => void;
  setAddingItemToTopic: (topicId: string | null) => void;
  handleCreateItem: (
    input: {
      descricao: string;
      topico_id?: string;
      entidade?: string;
      prazo?: string;
      ata_origem_numero?: number;
      ata_origem_data?: string;
    }
  ) => Promise<void>;
  handleUpdateItem: (
    itemId: string,
    input: {
      descricao?: string;
      entidade?: string;
      prazo?: string;
      topico_id?: string;
    }
  ) => Promise<void>;
  handleDeleteItem: (itemId: string) => Promise<void>;
  handleToggleItemStatus: (itemId: string, concluido: boolean) => Promise<void>;
  handleAddDesenvolvimento: (
    itemId: string,
    texto: string
  ) => Promise<void>;
  handleUpdateDesenvolvimento: (
    desenvolvimentoId: string,
    texto: string
  ) => Promise<void>;
  handleDeleteDesenvolvimento: (
    desenvolvimentoId: string
  ) => Promise<void>;
  getDesenvolvimentosByItemId: (itemId: string) => Desenvolvimento[];
  syncAtaOrigemNumero: (oldNum: number, newNum: number) => void;
  syncAtaOrigemData: (newDate: string) => void;
}

export function useItems({
  ataId,
  meetingNumber,
  meetingDate,
  initialItems,
  initialDesenvolvimentos,
  onUpdate,
  onChangeDetected,
}: UseItemsParams): UseItemsReturn {
  const [items, setItems] = useState<ItemAta[]>(initialItems);
  const [desenvolvimentos, setDesenvolvimentos] = useState<Desenvolvimento[]>(
    initialDesenvolvimentos
  );
  const [editingItem, setEditingItem] = useState<ItemAta | null>(null);
  const [addingItemToTopic, setAddingItemToTopic] = useState<string | null>(
    null
  );


  const getTopicSuffix = (numeroItem?: string) => {
    if (!numeroItem || !numeroItem.includes('.')) return Number.POSITIVE_INFINITY;
    const suffix = numeroItem.split('.').pop() ?? '';
    const parsed = parseInt(suffix, 10);
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  };

  const getStandaloneNumber = (numeroItem?: string) => {
    if (!numeroItem || !numeroItem.startsWith('A')) return Number.POSITIVE_INFINITY;
    const match = numeroItem.match(/^A(\d+)$/);
    if (!match) return Number.POSITIVE_INFINITY;
    const parsed = parseInt(match[1], 10);
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  };

  const renumberItemsAfterDelete = (currentItems: ItemAta[], deletedItem: ItemAta) => {
    if (deletedItem.topico_id) {
      const prefix = deletedItem.numero_item?.split('.')[0];
      if (!prefix) return currentItems;

      const topicItems = currentItems
        .filter((item) => item.topico_id === deletedItem.topico_id)
        .sort((a, b) => {
          const diff = getTopicSuffix(a.numero_item) - getTopicSuffix(b.numero_item);
          return diff !== 0 ? diff : Number(a.id) - Number(b.id);
        });

      const renumberMap = new Map<string, string>();
      topicItems.forEach((item, index) => {
        renumberMap.set(item.id, `${prefix}.${index + 1}`);
      });

      return currentItems.map((item) =>
        renumberMap.has(item.id)
          ? { ...item, numero_item: renumberMap.get(item.id) }
          : item
      );
    }

    const standaloneItems = currentItems
      .filter((item) => !item.topico_id && item.numero_item?.startsWith('A'))
      .sort((a, b) => {
        const diff = getStandaloneNumber(a.numero_item) - getStandaloneNumber(b.numero_item);
        return diff !== 0 ? diff : Number(a.id) - Number(b.id);
      });

    const renumberMap = new Map<string, string>();
    standaloneItems.forEach((item, index) => {
      renumberMap.set(item.id, `A${String(index + 1).padStart(2, '0')}`);
    });

    return currentItems.map((item) =>
      renumberMap.has(item.id)
        ? { ...item, numero_item: renumberMap.get(item.id) }
        : item
    );
  };


  useEffect(() => {
    setItems(initialItems);
    setDesenvolvimentos(initialDesenvolvimentos);
  }, [initialItems, initialDesenvolvimentos]);


  const itemsByTopic = useMemo(() => {
    const map = new Map<string, ItemAta[]>();
    items.forEach((item) => {
      const topicId = item.topico_id || "sem-topico";
      if (!map.has(topicId)) {
        map.set(topicId, []);
      }
      map.get(topicId)!.push(item);
    });
    return map;
  }, [items]);

  const handleCreateItem = useCallback(
    async (input: {
      descricao: string;
      topico_id?: string;
      entidade?: string;
      prazo?: string;
      ata_origem_numero?: number;
      ata_origem_data?: string;
    }) => {
      try {
        // Validação de descrição
        if (!input.descricao.trim()) {
          toast.error('Descrição do item é obrigatória. Descreva o que precisa ser feito.');
          return;
        }
        if (input.descricao.trim().length > LIMITS.ITEM_DESCRIPTION_MAX) {
          toast.error(`Descrição excedeu o limite de ${LIMITS.ITEM_DESCRIPTION_MAX} caracteres. Reduza o texto para poder salvar.`);
          return;
        }

        // Verifica limite de itens no tópico
        if (input.topico_id) {
          const topicItems = items.filter(i => i.topico_id === input.topico_id);
          if (topicItems.length >= LIMITS.MAX_ITEMS_PER_TOPIC) {
            toast.error(ERROR_MESSAGES.MAX_ITEMS_REACHED);
            return;
          }
        }

        const newItem = await itemAtaService.createItemAta(ataId, {
          descricao: input.descricao,
          concluido: false,
          topico_id: input.topico_id,
          entidade: input.entidade,
          prazo: input.prazo,
          ata_origem_numero: input.ata_origem_numero,
          ata_origem_data: input.ata_origem_data,
        });

        setItems([...items, newItem]);
        setAddingItemToTopic(null);
        toast.success("Item adicionado com sucesso.");

        // Notifica o autosave sobre a mudança
        onChangeDetected?.();
      } catch (error: any) {
        const errMsg = error.message || '';
        if (errMsg.includes('conexão') || errMsg.includes('internet')) {
          toast.error('Não foi possível adicionar o item — verifique sua conexão com a internet.');
        } else {
          toast.error(errMsg || 'Não foi possível adicionar o item. Verifique os dados e tente novamente.');
        }
      }
    },
    [ataId, items, onChangeDetected]
  );

  const handleUpdateItem = useCallback(
    async (
      itemId: string,
      input: {
        descricao?: string;
        entidade?: string;
        prazo?: string;
        topico_id?: string;
      }
    ) => {
      try {
        // Validações antes de enviar
        if (input.descricao !== undefined && !input.descricao.trim()) {
          toast.error('Descrição do item é obrigatória. Descreva o que precisa ser feito.');
          return;
        }
        if (input.descricao && input.descricao.trim().length > LIMITS.ITEM_DESCRIPTION_MAX) {
          toast.error(`Descrição excedeu o limite de ${LIMITS.ITEM_DESCRIPTION_MAX} caracteres. Reduza o texto para poder salvar.`);
          return;
        }
        if (input.entidade && input.entidade.length > LIMITS.ITEM_ENTITY_MAX) {
          toast.error(`Entidade/Responsável excedeu o limite de ${LIMITS.ITEM_ENTITY_MAX} caracteres.`);
          return;
        }

        const updatedItem = await itemAtaService.updateItemAta(itemId, input);
        setItems(items.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
        setEditingItem(null);
        toast.success("Item atualizado com sucesso.");

        // Notifica o autosave sobre a mudança
        onChangeDetected?.();
      } catch (error: any) {
        toast.error(error.message || "Erro ao atualizar item");
      }
    },
    [items, onChangeDetected]
  );

  const handleDeleteItem = useCallback(
    async (itemId: string) => {
      try {
        const deletedItem = items.find((item) => item.id === itemId);
        await itemAtaService.deleteItemAta(itemId);
        setItems((currentItems) => {
          const remainingItems = currentItems.filter((item) => item.id !== itemId);
          return deletedItem ? renumberItemsAfterDelete(remainingItems, deletedItem) : remainingItems;
        });
        setDesenvolvimentos(
          desenvolvimentos.filter((d) => d.item_id !== itemId)
        );
        if (editingItem?.id === itemId) {
          setEditingItem(null);
        }
        toast.success("Item excluído com sucesso.");

        // Notifica o autosave sobre a mudança
        onChangeDetected?.();
      } catch (error: any) {
        toast.error(error.message || 'Não foi possível excluir o item. Ele pode estar vinculado a outros registros.');
      }
    },
    [items, desenvolvimentos, editingItem, onChangeDetected]
  );

  const handleToggleItemStatus = useCallback(
    async (itemId: string, concluido: boolean) => {
      try {
        const updatedItem = await itemAtaService.updateItemAta(itemId, {
          concluido,
        });
        setItems(items.map((i) => (i.id === updatedItem.id ? updatedItem : i)));
        toast.success(
          concluido ? "Item marcado como concluído \u2713" : "Item reaberto como pendente"
        );

        // Notifica o autosave sobre a mudança
        onChangeDetected?.();
      } catch (error: any) {
        toast.error(error.message || "Erro ao atualizar item");
      }
    },
    [items, onChangeDetected]
  );

  const handleAddDesenvolvimento = useCallback(
    async (itemId: string, texto: string) => {
      if (!texto.trim()) {
        toast.error("Texto do desenvolvimento é obrigatório");
        return;
      }

      try {
        const novoDesenvolvimento = await desenvolvimentoService.createDesenvolvimento(
          itemId,
          {
            texto: texto.trim(),
            ata_id: ataId,
            ata_numero: meetingNumber,
            destacado: true,
          }
        );

        setDesenvolvimentos([...desenvolvimentos, novoDesenvolvimento]);
        
        const updatedItems = items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              ata_origem_numero: meetingNumber,
              ata_origem_data: meetingDate,
              ata: {
                numero: meetingNumber,
              },
            };
          }
          return item;
        });
        setItems(updatedItems);

        toast.success("Desenvolvimento adicionado com sucesso.");

        // Notifica o autosave sobre a mudança
        onChangeDetected?.();

      } catch (error: any) {
        toast.error(error.message || 'Não foi possível adicionar o desenvolvimento. Tente novamente.');
      }
    },
    [ataId, meetingNumber, meetingDate, desenvolvimentos, items, onChangeDetected]
  );

  const handleUpdateDesenvolvimento = useCallback(
    async (desenvolvimentoId: string, texto: string) => {
      if (!texto.trim()) {
        toast.error("Texto do desenvolvimento é obrigatório. Descreva o que foi feito.");
        return;
      }

      if (texto.trim().length > LIMITS.DEVELOPMENT_TEXT_MAX) {
        toast.error(`Texto excedeu o limite de ${LIMITS.DEVELOPMENT_TEXT_MAX} caracteres. Reduza o texto.`);
        return;
      }

      try {
        const updated = await desenvolvimentoService.updateDesenvolvimento(
          desenvolvimentoId,
          { texto: texto.trim() }
        );
        setDesenvolvimentos(
          desenvolvimentos.map((d) =>
            d.id === updated.id ? updated : d
          )
        );
        toast.success("Desenvolvimento atualizado com sucesso.");

        // Notifica o autosave sobre a mudança
        onChangeDetected?.();
      } catch (error: any) {
        toast.error(error.message || 'Não foi possível atualizar o desenvolvimento. Tente novamente.');
      }
    },
    [desenvolvimentos, onChangeDetected]
  );

  const handleDeleteDesenvolvimento = useCallback(
    async (desenvolvimentoId: string) => {
      try {
        await desenvolvimentoService.deleteDesenvolvimento(desenvolvimentoId);
        setDesenvolvimentos(
          desenvolvimentos.filter((d) => d.id !== desenvolvimentoId)
        );
        toast.success("Desenvolvimento excluído com sucesso.");

        // Notifica o autosave sobre a mudança
        onChangeDetected?.();
      } catch (error: any) {
        toast.error(error.message || 'Não foi possível excluir o desenvolvimento. Tente novamente.');
      }
    },
    [desenvolvimentos, onChangeDetected]
  );

  const getDesenvolvimentosByItemId = useCallback(
    (itemId: string): Desenvolvimento[] => {
      return desenvolvimentos
        .filter((d) => d.item_id === itemId)
        .sort((a, b) => a.ata_numero - b.ata_numero);
    },
    [desenvolvimentos]
  );

  const syncAtaOrigemNumero = useCallback(
    (oldNum: number, newNum: number) => {
      setItems((prev) =>
        prev.map((item) =>
          item.ata_origem_numero === oldNum
            ? { ...item, ata_origem_numero: newNum }
            : item
        )
      );
    },
    []
  );

  const syncAtaOrigemData = useCallback(
    (newDate: string) => {
      setItems((prev) =>
        prev.map((item) => ({ ...item, ata_origem_data: newDate }))
      );
    },
    []
  );

  return {
    items,
    desenvolvimentos,
    editingItem,
    addingItemToTopic,
    itemsByTopic,
    setEditingItem,
    setAddingItemToTopic,
    handleCreateItem,
    handleUpdateItem,
    handleDeleteItem,
    handleToggleItemStatus,
    handleAddDesenvolvimento,
    handleUpdateDesenvolvimento,
    handleDeleteDesenvolvimento,
    getDesenvolvimentosByItemId,
    syncAtaOrigemNumero,
    syncAtaOrigemData,
  };
}

